import { useCallback, useMemo, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ResponsiveDialog from '../UI/Dialogs/ResponsiveDialog';
import { useCreateBudgetScenario } from '../../hooks/api/budgetScenarios';
import { BudgetScenario } from '../../services/budgetScenario';
import BucketVersionFields from './BucketVersionFields';
import { scenarioNameError } from './scenarioMeta';

interface ScenarioDuplicateDialogProps {
    // The scenario to copy; null closes the dialog.
    scenario: BudgetScenario | null;
    onClose: () => void;
    // Called with the new scenario's id — the caller navigates to it so the copy can be edited.
    onCreated: (id: number) => void;
}

// The board's duplicate action ("duplicate to Budget xyz"): pick a name and the budget version to copy
// into, and the backend clones the metadata plus every monthly input in one transaction.
//
// Target versions are limited to the source's bucket, because the copied input rows are keyed to that
// bucket's worktypes / HEMT locations — a foreign bucket would yield a scenario that calculates to zero
// everywhere (the backend rejects it too). The period is copied as-is and can be adjusted afterwards.
//
// The caller keys this component by the scenario being duplicated, so opening it for a different row
// remounts it with fresh defaults. Deliberately not an effect: a reset effect would have to depend on
// `scenario` and `t`, and would wipe what the user typed on any re-render where either changes identity.
const ScenarioDuplicateDialog = ({ scenario, onClose, onCreated }: ScenarioDuplicateDialogProps) => {
    const { t } = useTranslation('budget');
    const [name, setName] = useState(() => (scenario ? t('scenarios.duplicateSuffix', { name: scenario.name }) : ''));
    const [versionId, setVersionId] = useState<number | ''>(() => scenario?.budgetVersionId ?? '');

    const { mutateAsync: createScenario, isLoading: isCreating } = useCreateBudgetScenario();

    const nameError = useMemo(() => scenarioNameError(name, t), [name, t]);
    const hasErrors = Boolean(nameError) || versionId === '';

    const handleConfirm = useCallback(async () => {
        if (!scenario || versionId === '' || hasErrors) return;
        try {
            const created = await createScenario({
                cloneFromId: scenario.id,
                name: name.trim(),
                description: scenario.description ?? null,
                // Immutable after create and type-specific inputs are being copied — the backend
                // enforces it matches the source too.
                type: scenario.type,
                budgetVersionId: versionId,
                startDate: scenario.startDate,
                endDate: scenario.endDate,
            });
            onCreated(created.id);
        } catch {
            // The mutation's onError already raised the toast; keep the dialog open for a retry.
        }
    }, [scenario, versionId, hasErrors, createScenario, name, onCreated]);

    return (
        <ResponsiveDialog
            open={scenario !== null}
            onClose={onClose}
            title={t('scenarios.duplicateTitle')}
            isConfirmationDialog
            onConfirm={handleConfirm}
            confirmButtonText={t('action.save')}
            disableConfirmButton={hasErrors}
            isLoading={isCreating}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1, minWidth: 400 }}>
                <Typography variant="body2" color="text.secondary">
                    <span>{t('scenarios.duplicateHint')}</span>
                </Typography>
                <TextField
                    label={t('field.scenarioName')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    size="small"
                    required
                    error={Boolean(nameError)}
                    helperText={nameError ?? undefined}
                    inputProps={{ className: 'notranslate' }}
                />
                {/* Same budget picker as the scenario form; the bucket is locked to the source's. */}
                <BucketVersionFields versionId={versionId} onChange={setVersionId} lockBucket />
            </Box>
        </ResponsiveDialog>
    );
};

export default ScenarioDuplicateDialog;
